/**
 * Input Mixin
 *
 * Contains general methods and props for Input components.
 */
import { mapGetters } from 'vuex';

export default {
  props: {
    /**
     * An Object containing information related to the input element
     */
    field: {
      type: Object,
      default() {
        return {};
      },
    },
    /**
     * The value to bind to the input, defaults to String values
     */
    value: {
      type: String,
      default() {
        return this.field.value || '';
      },
    },
  },
  computed: {
    strippedFieldTitle() {
      // Returns the field's title, with any HTML tags stripped out.
      return this.stripTags(this.field.title);
    },
    sanitizedFieldTitle() {
      // Return the help text for the field, sanitized, if any.
      return this.$sanitize(this.field.title || '');
    },
    sanitizedHelpText() {
      // Return the help text for the field, sanitized, if any.
      return this.$sanitize(this.field.help_text || '');
    },
    sanitizedAdditionalText() {
      // Return the additional text for the field, sanitized, if any.
      return this.$sanitize(this.field.additional_text || '');
    },
    productCountText() {
      // Returns the (x of y) text used to show how many products were requested
      if (this.formFields.length && this.formFields[0].product_id &&
          this.formFields[0].product_count > 1) {
        return `(${this.formFields[0].product_index} of ${this.formFields[0].product_count})`;
      }

      return '';
    },
    hasProduct() {
      // Returns if the field has a product and if there's product data in the store
      const hasProd = this.formFields.length && this.formFields[0].product_id &&
          this.productById(this.formFields[0].product_id) !== undefined;
      return hasProd;
    },
    hasCategory() {
      const hasCat = this.formFields.length && this.formFields[0].category_id &&
          this.productsLoaded && !this.formFields[0].product_id;
      return hasCat;
    },
    ...mapGetters('products', ['productById', 'productByCategoryId', 'productsLoaded']),
  },
  methods: {
    stripTags(str) {
      return this.$sanitize(str || '', {
        allowedTags: [],
      });
    },
  },
  inject: ['$validator'],
  data() {
    return {
      // The validation rules to apply to the input
      validationRules: {},
    };
  },
  created() {
    // Iterate over the different validation types and add them to the validationRules if
    // the field specifies it.
    const validationTypes = ['required', 'min_value', 'max_value', 'min_length', 'max_length', 'regex_pattern'];
    validationTypes.forEach((validationType) => {
      let veeValidationName = validationType;
      switch (validationType) {
        case 'min_length':
          veeValidationName = 'min';
          break;
        case 'max_length':
          veeValidationName = 'max';
          break;
        case 'regex_pattern':
          veeValidationName = 'regex';
          break;
        default:
          break;
      }
      if (this.field[validationType] !== null && this.field[validationType] !== undefined) {
        this.validationRules[veeValidationName] = this.field[validationType];
      }
    });
  },
};
